import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Phone, ShieldCheck, X } from "lucide-react";
import { PROFILI, NEEDS, CONTACT, needFromParam, slugNeed } from "../data/site";
import { openAiChat } from "../aichat";
import { useDialogA11y } from "../a11y";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/richieste-preventivo`;
const SEND_FAIL_MSG = "Non siamo riusciti a inviare la richiesta. Chiamaci al +39 06 232514 o scrivici in chat all'assistente AI";

const QuoteCtx = createContext({ openQuote: () => {} });
/* Ultimo elemento che ha aperto il pannello: alla chiusura il focus torna qui. */
let quoteOpenerEl = null;
export const useQuote = () => useContext(QuoteCtx);

/* Pulsante di richiesta preventivo: senza JavaScript porta alla pagina
   /preventivo; con JavaScript apre il pannello sovrapposto con il servizio
   eventualmente preselezionato. */
export function QuoteLink({ servizio = null, className, testid, children, onClick, openDelay = 0 }) {
  const { openQuote } = useQuote();
  const need = servizio ? needFromParam(servizio) : null;
  const href = `/preventivo${need ? `?servizio=${slugNeed(need)}` : ""}`;
  return (
    <a
      href={href}
      className={className}
      data-testid={testid}
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
        window.setTimeout(() => openQuote(need), openDelay);
      }}
    >
      {children}
    </a>
  );
}

/* Errore di invio: invito a chiamare o a scrivere in chat all'assistente AI. */
function ErrorBox({ tid }) {
  return (
    <div className="ferr" style={{ margin: "20px 0 0", display: "grid", gap: 14 }} data-testid={tid("form-error")}>
      <p style={{ margin: 0 }}>{SEND_FAIL_MSG}</p>
      <div className="fnav" style={{ margin: 0 }}>
        <a className="btn dark sm" href="tel:+3906232514" data-testid={tid("err-chiama")}>
          <Phone size={16} /> Chiama
        </a>
        <a
          className="btn white sm"
          style={{ boxShadow: "inset 0 0 0 2px var(--line)" }}
          href="#assistente-ai"
          onClick={(e) => { e.preventDefault(); openAiChat(); }}
          data-testid={tid("err-chat")}
        >
          Scrivi all'assistente AI
        </a>
      </div>
    </div>
  );
}

/* Modulo in due passaggi, unico e riutilizzabile (Contatti, pannello, /preventivo). */
export function QuoteForm({ preServizio = "", provenance = "", idPrefix = "f", tid = (s) => s, doneNode = null }) {
  const [fstep, setFstep] = useState(1);
  const [profilo, setProfilo] = useState("");
  const [need, setNeed] = useState(() => (NEEDS.includes(preServizio) ? preServizio : ""));
  const [form, setForm] = useState({ nome: "", azienda: "", citta: "", telefono: "", email: "", messaggio: "", sito: "", privacy: false });
  const [err, setErr] = useState("");
  const [sendFail, setSendFail] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "privacy" ? e.target.checked : e.target.value });
  const id = (k) => `${idPrefix}-${k}`;

  useEffect(() => {
    if (NEEDS.includes(preServizio)) setNeed(preServizio);
  }, [preServizio]);

  const goStep2 = () => {
    if (!profilo || !need) return setErr("Scegli per chi è il servizio e di cosa hai bisogno.");
    setErr("");
    setFstep(2);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefono.trim() || !form.email.trim()) return setErr("Compila nome, telefono ed email.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setErr("Inserisci un indirizzo email valido.");
    if (!form.privacy) return setErr("Devi accettare l'informativa privacy.");
    setErr("");
    setSending(true);
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, profilo, servizio: need, provenance: provenance || window.location.pathname }),
      });
      if (!res.ok) throw new Error("server");
      setDone(true);
    } catch {
      setErr(SEND_FAIL_MSG);
      setSendFail(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="form-card" data-testid={tid("preventivo-form")}>
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div key="ok" className="fok" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} data-testid={tid("form-success")}>
            <div className="big">
              <Check size={36} />
            </div>
            <h3>Richiesta inviata</h3>
            {doneNode || (
              <p style={{ color: "var(--muted)" }}>
                Grazie. Un nostro responsabile ti ricontatterà al più presto per il sopralluogo gratuito. Per urgenze, chiama il {CONTACT.phone}.
              </p>
            )}
          </motion.div>
        ) : fstep === 1 ? (
          <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            <p className="fstep">
              <ShieldCheck size={16} /> Passo 1 di 2
            </p>
            <p style={{ fontWeight: 700, margin: "0 0 12px" }}>Per chi è il servizio?</p>
            <div className="opt-group" role="group" aria-label="Per chi è il servizio">
              {PROFILI.map((p) => (
                <button key={p} type="button" className={`opt ${profilo === p ? "on" : ""}`} onClick={() => setProfilo(p)} data-testid={`profilo-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                  {p}
                </button>
              ))}
            </div>
            <p style={{ fontWeight: 700, margin: "26px 0 12px" }}>Di cosa hai bisogno?</p>
            <div className="opt-group" role="group" aria-label="Di cosa hai bisogno">
              {NEEDS.map((n) => (
                <button key={n} type="button" className={`opt ${need === n ? "on" : ""}`} onClick={() => setNeed(n)} data-testid={tid(`need-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`)}>
                  {n}
                </button>
              ))}
            </div>
            {err && <p className="ferr" style={{ marginTop: 20 }} data-testid={tid("form-error")}>{err}</p>}
            <div className="fnav" style={{ marginTop: 28 }}>
              <button type="button" className="btn dark" onClick={goStep2} data-testid={tid("form-continua")}>
                Continua <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            <p className="fstep">
              <ShieldCheck size={16} /> Passo 2 di 2
            </p>
            <form onSubmit={submit} noValidate>
              {/* Honeypot antispam: campo invisibile per gli umani, i bot lo compilano */}
              <input
                type="text"
                name="sito"
                value={form.sito}
                onChange={(e) => setForm({ ...form, sito: e.target.value })}
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="hp-field"
              />
              <div className="frow">
                <div className="field">
                  <label htmlFor={id("nome")}>Nome e cognome *</label>
                  <input id={id("nome")} value={form.nome} onChange={set("nome")} data-testid={tid("field-nome")} autoComplete="name" />
                </div>
                <div className="field">
                  <label htmlFor={id("azienda")}>Azienda (facoltativo)</label>
                  <input id={id("azienda")} value={form.azienda} onChange={set("azienda")} data-testid={tid("field-azienda")} autoComplete="organization" />
                </div>
                <div className="field">
                  <label htmlFor={id("citta")}>Città</label>
                  <input id={id("citta")} value={form.citta} onChange={set("citta")} data-testid={tid("field-citta")} autoComplete="address-level2" />
                </div>
                <div className="field">
                  <label htmlFor={id("telefono")}>Telefono *</label>
                  <input id={id("telefono")} type="tel" value={form.telefono} onChange={set("telefono")} data-testid={tid("field-telefono")} autoComplete="tel" />
                </div>
              </div>
              <div className="field">
                <label htmlFor={id("email")}>Email *</label>
                <input id={id("email")} type="email" value={form.email} onChange={set("email")} data-testid={tid("field-email")} autoComplete="email" />
              </div>
              <div className="field">
                <label htmlFor={id("messaggio")}>Messaggio (facoltativo)</label>
                <textarea id={id("messaggio")} value={form.messaggio} onChange={set("messaggio")} data-testid={tid("field-messaggio")} />
              </div>
              <label className="check" data-testid={tid("field-privacy-wrap")}>
                <input type="checkbox" checked={form.privacy} onChange={set("privacy")} data-testid={tid("field-privacy")} />
                <span>Ho letto l'informativa privacy e acconsento al trattamento dei dati</span>
              </label>
              {err && !sendFail && <p className="ferr" data-testid={tid("form-error")}>{err}</p>}
              {err && sendFail && <ErrorBox tid={tid} />}
              <div className="fnav">
                <button type="button" className="btn white sm" style={{ boxShadow: "inset 0 0 0 2px var(--line)" }} onClick={() => setFstep(1)} data-testid={tid("form-indietro")}>
                  <ArrowLeft size={16} /> Indietro
                </button>
                <button type="submit" className="btn dark sm" disabled={sending} data-testid={tid("form-invia")} style={{ opacity: sending ? 0.6 : 1 }}>
                  {sending ? "Invio…" : "Invia la richiesta"}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Pannello sovrapposto: a destra 520px su computer, a tutto schermo su telefono.
   La pagina sotto non scorre e alla chiusura resta dov'era. */
function Panel({ servizio, onClose }) {
  const reduce = useReducedMotion();
  const provenance = useRef(window.location.pathname + window.location.search);
  const panelRef = useRef(null);
  useDialogA11y(panelRef, true, onClose);

  useEffect(() => {
    return () => {
      /* Alla chiusura il focus torna al pulsante che ha aperto il pannello. */
      const el = quoteOpenerEl;
      quoteOpenerEl = null;
      if (el && el.focus) el.focus();
    };
  }, []);

  useEffect(() => {
    const savedY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      const was = document.body.style.position === "fixed";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      if (was) window.scrollTo(0, savedY);
      document.documentElement.style.scrollBehavior = "";
    };
  }, [onClose]);

  return (
    <motion.div
      className="qoverlay"      initial={{ opacity: reduce ? 1 : 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: reduce ? 1 : 0 }}
      transition={{ duration: reduce ? 0 : 0.2 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="quote-overlay"
    >
      <motion.aside
        ref={panelRef}
        className="qpanel"
        initial={{ x: reduce ? 0 : "100%" }}
        animate={{ x: 0 }}
        exit={{ x: reduce ? 0 : "100%" }}
        transition={{ duration: reduce ? 0 : 0.32, ease: [0.2, 0.7, 0.2, 1] }}
        role="dialog"
        aria-modal="true"
        aria-label="Richiedi un preventivo"
        data-testid="quote-panel"
      >
        <header className="qpanel-head">
          <p className="kick" style={{ margin: 0 }}>
            Preventivo gratuito
          </p>
          <button type="button" className="qclose" aria-label="Chiudi il pannello" onClick={onClose} data-testid="quote-close" autoFocus>
            <X size={22} />
          </button>
        </header>
        <div className="qpanel-body">
          <QuoteForm
            preServizio={servizio || ""}
            provenance={provenance.current}
            idPrefix="qp"
            tid={(s) => `panel-${s}`}
            doneNode={
              <p style={{ color: "var(--muted)" }}>
                Grazie, abbiamo ricevuto la tua richiesta. Ti richiamiamo al più presto.
              </p>
            }
          />
          <button type="button" className="btn dark sm qdone" onClick={onClose} data-testid="panel-done-close">
            Chiudi
          </button>
        </div>
      </motion.aside>
    </motion.div>
  );
}

export function QuoteProvider({ children }) {
  const [state, setState] = useState(null);
  const openQuote = useCallback((servizio = null) => {
    quoteOpenerEl = document.activeElement || null;
    setState({ servizio });
  }, []);
  const close = useCallback(() => setState(null), []);
  return (
    <QuoteCtx.Provider value={{ openQuote }}>
      {children}
      <AnimatePresence>{state && <Panel key="quote-panel" servizio={state.servizio} onClose={close} />}</AnimatePresence>
    </QuoteCtx.Provider>
  );
}
